import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/api';

// 📊 Admin card for the weekly usage snapshot (10-17, USER_ACTIVITY_REPORT_HANDOFF_2026_10_17.md §4 #10).
// Backend = adminWeeklyStats in handler.js (admin-auth, src/ChatGame/weeklyStats.js): one row of
// counts per week, written by the cronAutoStep rider after each week closes (Tue 22:00 UTC). Rows
// hold counts only, never an address. Room and agent names are user-made: they render as TEXT only.

// Chart roles on the card surface (#151b26), checked with the dataviz validator in dark mode:
// s1-s3 = categorical slots (adjacent CVD ΔE ≥ 9.4, all ≥ 3:1); muted = the de-emphasis gray
// (ΔE 15.8 from s1, 3:1 on the surface).
const C = {
  s1: '#3987e5', s2: '#d95926', s3: '#199e70', muted: '#646e7f',
  ink: '#e8edf4', ink2: '#9aa7b8', ink3: '#8b98a9', grid: '#232c3b', base: '#39455a',
  up: '#0ca30c', down: '#ec835a', tile: '#0f141c',
};
const WEEKS_SHOWN = 12;
// A claimed run with no receipt after this long has stopped (the worker's own cap is 10 minutes).
const STALL_MS = 15 * 60e3;
const PLOT_H = 140;

const fmtDay = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const fmtStamp = (iso) => `${fmtDay(iso)}, ${new Date(iso).toISOString().slice(11, 16)} UTC`;
const num = (n) => Number(n || 0).toLocaleString('en-US');
const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const spendOf = (w) => (w.spend_house || 0) + (w.spend_other || 0) + (w.spend_unattr || 0);
const CRAWLER_NAMES = { google: 'Google', meta: 'Meta', microsoft: 'Microsoft', anthropic: 'Anthropic range', burst: 'scanners', unconnected: 'never connected' };

function niceAxis(max, target = 4) {
  if (!(max > 0)) return { top: 1, step: 1 };
  const raw = max / target;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw);
  return { top: Math.ceil(max / step) * step, step };
}

function Tile({ label, value, delta, goodWhenUp, deltaFmt = num, note }) {
  let deltaEl = null;
  if (delta != null && Number.isFinite(delta)) {
    const up = delta > 0;
    const color = delta === 0 || goodWhenUp == null ? C.ink2 : (up === goodWhenUp ? C.up : C.down);
    const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
    deltaEl = (
      <div style={{ fontSize: 12, color, marginTop: 2 }}>
        {delta > 0 ? '▲' : delta < 0 ? '▼' : '•'} {sign}{deltaFmt(Math.abs(delta))} vs last week
      </div>
    );
  }
  return (
    <div role="group" aria-label={label} style={{ background: C.tile, border: '1px solid #26303f', borderRadius: 8, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontSize: 12, color: C.ink2 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 650, color: C.ink, lineHeight: 1.25 }}>{value}</div>
      {deltaEl}
      {note && <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 4, lineHeight: 1.4 }}>{note}</div>}
    </div>
  );
}

function Legend({ series }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: C.ink2, margin: '6px 0 8px' }}>
      {series.map((s) => (
        <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: 'inline-block' }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

// Stacked columns, oldest week first. Every value is also in the table under the chart.
function Columns({ title, sub, weeks, series, fmt = num }) {
  const [hover, setHover] = useState(null);
  const n = weeks.length;
  const totals = weeks.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] || 0), 0));
  const max = Math.max(0, ...totals);
  const { top, step } = niceAxis(max);
  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);
  const pct = (v) => (v / top) * 100;
  const maxI = totals.indexOf(max);
  const labelled = new Set([n - 1, maxI]);
  const tipRows = (i) => series.map((s) => ({ color: s.color, label: s.label, value: fmt(s.values[i] || 0) })).reverse();

  return (
    <figure style={{ margin: 0, minWidth: 0 }}>
      <figcaption>
        <div style={{ fontSize: 14, fontWeight: 650, color: C.ink }}>{title}</div>
        <div style={{ fontSize: 12, color: C.ink3, marginTop: 2 }}>{sub}</div>
      </figcaption>
      {series.length > 1 && <Legend series={series} />}
      <div style={{ display: 'flex', gap: 6, marginTop: series.length > 1 ? 0 : 10 }}>
        <div aria-hidden="true" style={{ position: 'relative', width: 34, height: PLOT_H, flex: 'none' }}>
          {ticks.map((v) => (
            <span key={v} style={{ position: 'absolute', right: 0, bottom: `calc(${pct(v)}% - 7px)`, fontSize: 10.5, color: C.ink3, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(v)}
            </span>
          ))}
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 0, height: PLOT_H }} onMouseLeave={() => setHover(null)}>
          {ticks.map((v) => (
            <div key={v} style={{ position: 'absolute', left: 0, right: 0, bottom: `${pct(v)}%`, borderTop: `1px solid ${v === 0 ? C.base : C.grid}` }} />
          ))}
          <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
            {weeks.map((w, i) => {
              const aria = `Week ending ${fmtDay(w.end)}: ${series.map((s) => `${s.label} ${fmt(s.values[i] || 0)}`).join(', ')}`;
              return (
                <div
                  key={w.end}
                  tabIndex={0}
                  aria-label={aria}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  style={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', outline: 'none', cursor: 'default' }}
                >
                  <div style={{
                    width: 'min(24px, 62%)', height: `${pct(totals[i])}%`, display: 'flex', flexDirection: 'column-reverse', gap: totals[i] ? 2 : 0,
                    filter: hover === i ? 'brightness(1.18)' : 'none',
                  }}>
                    {series.map((s, si) => {
                      const v = s.values[i] || 0;
                      if (!v) return null;
                      const isTop = series.slice(si + 1).every((t) => !(t.values[i] || 0));
                      return <div key={s.key} style={{ flex: v, minHeight: 1, background: s.color, borderRadius: isTop ? '4px 4px 0 0' : 0 }} />;
                    })}
                  </div>
                  {labelled.has(i) && totals[i] > 0 && (
                    <span style={{ position: 'absolute', bottom: `calc(${pct(totals[i])}% + 3px)`, fontSize: 11, color: C.ink, whiteSpace: 'nowrap' }}>
                      {fmt(totals[i])}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {hover != null && (
            <div role="tooltip" style={{
              position: 'absolute', top: 4, zIndex: 2, pointerEvents: 'none', background: '#0b0f15', border: '1px solid #39455a',
              borderRadius: 6, padding: '6px 9px', fontSize: 12, color: C.ink, whiteSpace: 'nowrap',
              ...(hover < n / 2 ? { left: `calc(${((hover + 1) / n) * 100}% + 4px)` } : { right: `calc(${((n - hover) / n) * 100}% + 4px)` }),
            }}>
              <div style={{ color: C.ink2, marginBottom: 3 }}>Week ending {fmtDay(weeks[hover].end)}</div>
              {tipRows(hover).map((r) => (
                <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i style={{ width: 10, height: 2, background: r.color, display: 'inline-block' }} />
                  <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{r.value}</strong>
                  <span style={{ color: C.ink2 }}>{r.label}</span>
                </div>
              ))}
              {series.length > 1 && <div style={{ color: C.ink2, marginTop: 3 }}>Total <strong style={{ color: C.ink }}>{fmt(totals[hover])}</strong></div>}
            </div>
          )}
        </div>
      </div>
      <div aria-hidden="true" style={{ display: 'flex', marginLeft: 40, marginTop: 4 }}>
        {weeks.map((w, i) => (
          <span key={w.end} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: C.ink3, lineHeight: 1.2, whiteSpace: 'nowrap', visibility: n > 8 && i % 2 !== (n - 1) % 2 ? 'hidden' : 'visible' }}>
            {fmtDay(w.end).split(' ')[0]}<br />{fmtDay(w.end).split(' ')[1]}
          </span>
        ))}
      </div>
      <details style={{ marginTop: 6 }}>
        <summary style={{ cursor: 'pointer', fontSize: 11.5, color: C.ink3 }}>Show as table</summary>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                <th style={TH}>Week ending</th>
                {series.map((s) => <th key={s.key} style={{ ...TH, textAlign: 'right' }}>{s.label}</th>)}
                {series.length > 1 && <th style={{ ...TH, textAlign: 'right' }}>Total</th>}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w, i) => (
                <tr key={w.end}>
                  <td style={TD}>{fmtDay(w.end)}</td>
                  {series.map((s) => <td key={s.key} style={{ ...TD, textAlign: 'right' }}>{fmt(s.values[i] || 0)}</td>)}
                  {series.length > 1 && <td style={{ ...TD, textAlign: 'right' }}>{fmt(totals[i])}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

const TH = { textAlign: 'left', padding: '4px 10px 4px 0', fontSize: 11, color: C.ink3, fontWeight: 600, whiteSpace: 'nowrap' };
const TD = { padding: '3px 10px 3px 0', color: '#dde3ea', whiteSpace: 'nowrap' };

// 🌍 10-23 (Nina's "C"): where the week's people came from, totals only (weeklyGeo.js looks each
// person's address up on ipwho.is; the row keeps countries, US states and network kinds, never an IP).
// jsonb hands an object back in ITS key order (shortest key first), never by count: sort here.
function GeoList({ title, counts, max = 8 }) {
  const all = Object.entries(counts || {}).sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  const rows = all.slice(0, max);
  const more = all.length - rows.length;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: C.ink2, marginBottom: 2 }}>{title}</div>
      {rows.length === 0 ? <div style={{ fontSize: 12.5, color: C.ink3 }}>None</div> : (
        <table aria-label={title} style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
          <tbody>
            {rows.map(([name, n]) => (
              <tr key={name}>
                <td style={{ ...TD, whiteSpace: 'normal', wordBreak: 'break-word' }}>{name}</td>
                <td style={{ ...TD, textAlign: 'right', paddingRight: 0 }}>{num(n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {more > 0 && <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 2 }}>+{num(more)} more</div>}
    </div>
  );
}

function WhereFrom({ geo, geoRun }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 14, fontWeight: 650, color: C.ink, marginBottom: 4 }}>Where people came from</div>
      {!geo ? (
        <div style={{ fontSize: 12.5, color: C.ink3 }}>
          {geoRun
            ? `Not looked up yet: ${geoRun.error}. Next try after ${fmtStamp(geoRun.next_try_at)}.`
            : 'Not looked up yet for this week.'}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: C.ink2, marginBottom: 6 }}>
            {num(geo.located)} of {num(geo.people)} people located · {num(Object.keys(geo.countries || {}).filter((c) => c !== 'Unknown').length)} countries
            {geo.unresolved ? ` · ${num(geo.unresolved)} not answered` : ''}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px 28px' }}>
            <GeoList title="Countries" counts={geo.countries} />
            <GeoList title="US states" counts={geo.us_states} />
            <GeoList title="Connection" counts={geo.network} max={4} />
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminWeeklyStats({ adminFetch, adminKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminWeeklyStats=true&weeks=${WEEKS_SHOWN}`);
      const json = await res.json();
      if (json.success) { setData(json); setError(null); } else setError(json.error || 'unavailable');
    } catch (err) {
      setError(err.message);
    }
  }, [adminKey, adminFetch]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div style={{ fontSize: 12.5, color: '#d98a8a' }}>📊 {error}</div>;
  if (!data) return <div style={{ fontSize: 12.5, color: C.ink2 }}>Loading…</div>;

  const run = (data.runs || [])[0] || null;
  const runText = (r) => {
    const wk = `week ending ${fmtDay(r.week_end)}`;
    const tries = r.max_tries ? ` (try ${r.tries} of ${r.max_tries})` : '';
    if (r.finished_at) return `Last run: ${wk}, wrote ${r.written} week${r.written === 1 ? '' : 's'} in ${(r.ms / 1000).toFixed(1)} s (${fmtStamp(r.finished_at)}).`;
    if (r.error && r.max_tries && r.tries >= r.max_tries) return `⚠ Gave up on the ${wk} after ${r.tries} tries: ${r.error}. Next week's run fills it in.`;
    if (r.error) return `⚠ Last run failed${tries}, ${wk}: ${r.error}. It tries again an hour after it started.`;
    if (Date.now() - new Date(r.claimed_at).getTime() > STALL_MS) return `⚠ The run for the ${wk} stopped without finishing${tries}. It tries again an hour after it started.`;
    return `Running now: ${wk} (started ${fmtStamp(r.claimed_at)}).`;
  };
  const runLine = run && (
    <div style={{ fontSize: 12, color: run.finished_at || !runText(run).startsWith('⚠') ? C.ink3 : C.down, marginTop: 6 }}>
      {runText(run)}
    </div>
  );

  const rows = (data.weeks || []).map((r) => r.stats).filter(Boolean).reverse(); // oldest first
  if (!rows.length) {
    return (
      <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.55 }}>
        No weeks yet.{' '}
        {run ? null : <>The weekly job is off until <code>WEEKLY_STATS_ENABLED=true</code> is set on cronAutoStep. Its first run fills in every week since Aug 18.</>}
        {runLine}
      </div>
    );
  }

  const w = rows[rows.length - 1];
  const p = rows.length > 1 ? rows[rows.length - 2] : null;
  const d = (k, f = (x) => x[k]) => (p ? f(w) - f(p) : null);
  const share = w.msgs_people ? Math.round((100 * w.msgs_top_connection) / w.msgs_people) : null;
  const houseShare = spendOf(w) ? Math.round((100 * w.spend_house) / spendOf(w)) : null;
  const crawlerNote = Object.entries(w.crawlers || {}).sort((a, b) => b[1] - a[1]).slice(0, 3)
    .map(([k, v]) => `${CRAWLER_NAMES[k] || k} ${num(v)}`).join(' · ');

  return (
    <div style={{ color: '#cfd8e6' }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 10 }}>
        <strong style={{ color: C.ink, fontSize: 14 }}>Week ending {fmtStamp(w.end)}</strong>
        <button type="button" onClick={load} style={{ padding: '2px 8px', fontSize: 11.5, borderRadius: 5, border: '1px solid #39455a', background: '#151d29', color: '#cfd8e6', cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
        <Tile label="People" value={num(w.people)} delta={d('people')} goodWhenUp
          note={`${num(w.new_people)} new · ${num(w.returning)} returning${w.dwell_median_s != null ? ` · median stay ${w.dwell_median_s} s` : ''}`} />
        <Tile label="People who posted" value={num(w.people_posting)} delta={d('people_posting')} goodWhenUp
          note={`in ${num(w.rooms_active)} room${w.rooms_active === 1 ? '' : 's'}`} />
        <Tile label="Messages from people" value={num(w.msgs_people)} delta={d('msgs_people')} goodWhenUp
          note={share != null ? `${share}% from the most active connection` : null} />
        <Tile label="Regulars" value={num(w.regulars_3plus)} delta={d('regulars_3plus')} goodWhenUp
          note="acted in 3+ of the last 8 weeks" />
        <Tile label="Crawler seats" value={num(w.crawler_seats)} delta={d('crawler_seats')} note={crawlerNote || null} />
        <Tile label="AI spend" value={usd(spendOf(w))} delta={d(null, spendOf)} deltaFmt={usd}
          note={houseShare != null ? `the House ${houseShare}%` : null} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: '22px 28px', marginTop: 20 }}>
        <Columns
          title="People per week"
          sub="Connections that aren't crawlers or ours"
          weeks={rows}
          series={[
            { key: 'new', label: 'New', color: C.s1, values: rows.map((r) => r.new_people) },
            { key: 'ret', label: 'Returning', color: C.s2, values: rows.map((r) => r.returning) },
          ]}
        />
        <Columns
          title="Messages from people"
          sub="One connection vs everyone else"
          weeks={rows}
          series={[
            { key: 'rest', label: 'Everyone else', color: C.s1, values: rows.map((r) => Math.max(0, r.msgs_people - r.msgs_top_connection)) },
            { key: 'top', label: 'Most active connection', color: C.muted, values: rows.map((r) => r.msgs_top_connection) },
          ]}
        />
        <Columns
          title="New seats: people vs crawlers"
          sub="Crawlers mint a seat in every room they render"
          weeks={rows}
          series={[
            { key: 'people', label: 'People', color: C.s1, values: rows.map((r) => r.person_seats) },
            { key: 'crawlers', label: 'Crawlers', color: C.muted, values: rows.map((r) => r.crawler_seats) },
          ]}
        />
        <Columns
          title="AI spend"
          sub="Estimated from the spend log"
          weeks={rows}
          fmt={usd}
          series={[
            { key: 'house', label: 'The House', color: C.s1, values: rows.map((r) => r.spend_house) },
            { key: 'other', label: 'Other rooms', color: C.s2, values: rows.map((r) => r.spend_other) },
            { key: 'unattr', label: 'Unattributed', color: C.s3, values: rows.map((r) => r.spend_unattr) },
          ]}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '18px 28px', marginTop: 22 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 650, color: C.ink, marginBottom: 4 }}>Rooms that drew people</div>
          {(w.top_rooms || []).length === 0 ? <div style={{ fontSize: 12.5, color: C.ink2 }}>None this week.</div> : (
            <table style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
              <tbody>
                {w.top_rooms.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...TD, whiteSpace: 'normal', wordBreak: 'break-word', color: r.name == null ? C.ink3 : TD.color }}>
                      {r.name == null ? 'A private room' : (r.name || 'Untitled room')}
                    </td>
                    <td style={{ ...TD, textAlign: 'right', paddingRight: 0 }}>{num(r.people)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 4 }}>People who got a new seat there this week.</div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 650, color: C.ink, marginBottom: 4 }}>Outside AIs</div>
          <div style={{ fontSize: 12.5, color: C.ink2, marginBottom: 4 }}>
            {num(w.agents_posting)} posted · {num(w.agents_new)} signed up · {num(w.agent_msgs)} messages
          </div>
          {(w.top_agents || []).length > 0 && (
            <table style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
              <tbody>
                {w.top_agents.map((a) => (
                  <tr key={a.id8}>
                    <td style={{ ...TD, whiteSpace: 'normal', wordBreak: 'break-word' }}>{a.name || a.id8}</td>
                    <td style={{ ...TD, textAlign: 'right', paddingRight: 0 }}>{num(a.msgs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 650, color: C.ink, marginBottom: 4 }}>Safety and feedback</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <tbody>
              {[
                ['Crisis card shown', w.crisis_triggers],
                ['Community reports', w.modreports],
                ['Bug reports', w.bugs],
                ['Event feedback answers', w.feedback],
                ['New rooms by people', w.rooms_new_people],
              ].map(([label, v]) => (
                <tr key={label}>
                  <td style={TD}>{label}</td>
                  <td style={{ ...TD, textAlign: 'right', paddingRight: 0 }}>{num(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <WhereFrom geo={w.geo} geoRun={data.geo_run} />

      <p style={{ fontSize: 11.5, color: C.ink3, margin: '16px 0 0', lineHeight: 1.5, maxWidth: 860 }}>
        A person = one connection on a person seat that isn't a crawler or ours. Counts are a floor:
        someone who returns to an old seat and only reads leaves no new row. Weeks end Tuesday 22:00 UTC.
      </p>
      {runLine}
    </div>
  );
}
